import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { phoneSystemAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Phone, Search, Plus, Star, Trash2, ArrowRight, Loader2, PhoneIncoming, PhoneOutgoing, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { PortInWizard, PortRequestDetail } from '../components/PortInWizard';

// ── Number Search & Purchase Dialog ──
const SearchDialog = ({ open, onClose, onPurchased }) => {
  const [areaCode, setAreaCode] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(null);

  const handleSearch = async () => {
    if (!areaCode || areaCode.length < 3) {
      toast.error('Enter a valid 3-digit area code');
      return;
    }
    setLoading(true);
    try {
      const { data } = await phoneSystemAPI.searchNumbers({ area_code: areaCode, limit: 10 });
      setResults(data);
      if (data.length === 0) toast.info('No numbers found for that area code');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (number) => {
    setPurchasing(number.phone_number);
    try {
      await phoneSystemAPI.purchaseNumber({
        phone_number: number.phone_number,
        sms_enabled: true,
        voice_enabled: true,
      });
      toast.success(`Purchased ${number.friendly_name || number.phone_number}`);
      onPurchased();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="search-numbers-dialog">
        <DialogHeader>
          <DialogTitle>Search Available Numbers</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2">
          <Input
            placeholder="Area code (e.g. 512)"
            value={areaCode}
            onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            data-testid="area-code-input"
          />
          <Button onClick={handleSearch} disabled={loading} data-testid="search-numbers-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1 mt-2">
          {results.map((n) => (
            <div key={n.phone_number} className="flex items-center justify-between px-3 py-2.5 rounded-md hover:bg-muted/50 transition-colors">
              <div>
                <p className="font-mono font-medium text-sm">{n.friendly_name || n.phone_number}</p>
                <p className="text-xs text-muted-foreground">{n.locality}{n.region ? `, ${n.region}` : ''}</p>
              </div>
              <Button
                size="sm"
                onClick={() => handlePurchase(n)}
                disabled={purchasing === n.phone_number}
                data-testid={`purchase-btn-${n.phone_number}`}
              >
                {purchasing === n.phone_number ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Buy'}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Status color map ──
const statusColor = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  submitted: 'bg-blue-100 text-blue-700 border-blue-200',
  pending_review: 'bg-amber-100 text-amber-700 border-amber-200',
  waiting_for_signature: 'bg-purple-100 text-purple-700 border-purple-200',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
  docs_needed: 'bg-orange-100 text-orange-700 border-orange-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  in_review: 'bg-blue-100 text-blue-700 border-blue-200',
};

const statusLabel = {
  draft: 'Draft', submitted: 'Submitted', pending_review: 'Pending Review',
  waiting_for_signature: 'Awaiting LOA', in_progress: 'In Progress',
  docs_needed: 'Action Needed', approved: 'Approved', scheduled: 'Scheduled',
  completed: 'Completed', rejected: 'Rejected', failed: 'Failed',
  cancelled: 'Cancelled', pending: 'Pending', in_review: 'In Review',
};

// ── Main Page ──
export default function PhoneNumbers() {
  const { isOwner } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'numbers';
  const startWizard = searchParams.get('startWizard') === 'true';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [numbers, setNumbers] = useState([]);
  const [portRequests, setPortRequests] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [portWizardOpen, setPortWizardOpen] = useState(startWizard);
  const [selectedPortRequest, setSelectedPortRequest] = useState(null);

  const load = useCallback(async () => {
    try {
      const [numRes, portRes, usageRes] = await Promise.all([
        phoneSystemAPI.numbers(),
        phoneSystemAPI.portRequests(),
        phoneSystemAPI.usage(),
      ]);
      setNumbers(numRes.data);
      setPortRequests(portRes.data);
      setUsage(usageRes.data);
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error('Failed to load phone data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSetPrimary = async (id) => {
    try {
      await phoneSystemAPI.setPrimary(id);
      toast.success('Primary number updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set primary');
    }
  };

  const handleRelease = async (id) => {
    if (!window.confirm('Release this number? It will no longer receive calls/SMS.')) return;
    try {
      await phoneSystemAPI.releaseNumber(id);
      toast.success('Number released');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to release');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="phone-numbers-loading">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="phone-numbers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
              <Phone className="w-5 h-5 md:w-6 md:h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">Phone Numbers</h1>
              <p className="text-xs md:text-sm text-muted-foreground">Manage your business phone numbers</p>
            </div>
          </div>
          {isOwner && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { setActiveTab('porting'); setPortWizardOpen(true); }} data-testid="port-number-btn">
                <ArrowRight className="w-4 h-4 mr-1.5" /> Port Number
              </Button>
              <Button size="sm" onClick={() => setSearchOpen(true)} data-testid="buy-number-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Buy Number
              </Button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList data-testid="phone-tabs">
            <TabsTrigger value="numbers" data-testid="tab-numbers">Numbers ({numbers.length})</TabsTrigger>
            <TabsTrigger value="porting" data-testid="tab-porting">Port Requests ({portRequests.length})</TabsTrigger>
            <TabsTrigger value="usage" data-testid="tab-usage">Usage</TabsTrigger>
          </TabsList>

          {/* ── Numbers Tab ── */}
          <TabsContent value="numbers" className="mt-4">
            {numbers.length === 0 ? (
              <div className="border rounded-lg py-16 text-center text-muted-foreground">
                <Phone className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No phone numbers configured yet.</p>
                {isOwner && (
                  <Button className="mt-4" size="sm" onClick={() => setSearchOpen(true)} data-testid="empty-buy-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Buy Your First Number
                  </Button>
                )}
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                {/* Desktop table */}
                <table className="w-full hidden sm:table" data-testid="phone-numbers-table">
                  <thead>
                    <tr className="border-b text-xs">
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Number</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Label</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Capabilities</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Status</th>
                      <th className="text-right py-2.5 px-4 font-medium text-muted-foreground w-24"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {numbers.map((n) => (
                      <tr key={n.id} className="border-b last:border-b-0 hover:bg-muted/50 transition-colors" data-testid={`number-row-${n.id}`}>
                        <td className="py-2.5 px-4">
                          <span className="font-mono text-sm font-medium">{n.phone_number}</span>
                        </td>
                        <td className="py-2.5 px-4">
                          <span className="text-sm text-muted-foreground">{n.friendly_name || '—'}</span>
                        </td>
                        <td className="py-2.5 px-4">
                          <div className="flex gap-1.5">
                            {n.sms_enabled && <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">SMS</Badge>}
                            {n.voice_enabled && <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">Voice</Badge>}
                          </div>
                        </td>
                        <td className="py-2.5 px-4">
                          {n.is_primary ? (
                            <Badge variant="secondary" className="text-xs font-normal">
                              <Star className="w-3 h-3 mr-1 fill-current" /> Primary
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">Active</span>
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-right">
                          {isOwner && (
                            <div className="flex items-center justify-end gap-0.5">
                              {!n.is_primary && (
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleSetPrimary(n.id)} title="Set as primary" data-testid={`set-primary-${n.id}`}>
                                  <Star className="w-3.5 h-3.5 text-muted-foreground" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive/60 hover:text-destructive" onClick={() => handleRelease(n.id)} title="Release number" data-testid={`release-${n.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile list */}
                <div className="sm:hidden divide-y">
                  {numbers.map((n) => (
                    <div key={n.id} className="flex items-center justify-between px-4 py-3" data-testid={`number-card-${n.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium">{n.phone_number}</span>
                          {n.is_primary && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Primary</Badge>}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {n.friendly_name && <span className="text-xs text-muted-foreground">{n.friendly_name}</span>}
                          {n.sms_enabled && <Badge variant="outline" className="text-[9px] px-1 py-0">SMS</Badge>}
                          {n.voice_enabled && <Badge variant="outline" className="text-[9px] px-1 py-0">Voice</Badge>}
                        </div>
                      </div>
                      {isOwner && (
                        <div className="flex items-center gap-0.5 ml-2 shrink-0">
                          {!n.is_primary && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleSetPrimary(n.id)} data-testid={`set-primary-${n.id}`}>
                              <Star className="w-3.5 h-3.5 text-muted-foreground" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive/60 hover:text-destructive" onClick={() => handleRelease(n.id)} data-testid={`release-${n.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ── Port Requests Tab ── */}
          <TabsContent value="porting" className="mt-4">
            {portWizardOpen ? (
              <PortInWizard
                onClose={() => setPortWizardOpen(false)}
                onCreated={load}
              />
            ) : selectedPortRequest ? (
              <PortRequestDetail
                request={selectedPortRequest}
                onRefresh={async () => {
                  const res = await phoneSystemAPI.portRequest(selectedPortRequest.id);
                  setSelectedPortRequest(res.data);
                  load();
                }}
                onBack={() => setSelectedPortRequest(null)}
              />
            ) : portRequests.length === 0 ? (
              <div className="border rounded-lg py-16 text-center text-muted-foreground">
                <ArrowRight className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No port requests yet.</p>
                {isOwner && (
                  <Button className="mt-4" size="sm" onClick={() => setPortWizardOpen(true)} data-testid="start-port-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Port Your Numbers
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {isOwner && (
                  <div className="flex justify-end">
                    <Button size="sm" onClick={() => setPortWizardOpen(true)} data-testid="new-port-btn">
                      <Plus className="w-4 h-4 mr-1" /> New Port Request
                    </Button>
                  </div>
                )}
                <div className="border rounded-lg overflow-hidden">
                  {/* Desktop table */}
                  <table className="w-full hidden sm:table" data-testid="port-requests-table">
                    <thead>
                      <tr className="border-b text-xs">
                        <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Numbers</th>
                        <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Carrier</th>
                        <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Status</th>
                        <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Created</th>
                        <th className="text-right py-2.5 px-4 font-medium text-muted-foreground w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {portRequests.map((pr) => {
                        const nums = pr.phone_numbers || (pr.phone_number ? [pr.phone_number] : []);
                        return (
                          <tr key={pr.id} className="border-b last:border-b-0 hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => setSelectedPortRequest(pr)} data-testid={`port-request-${pr.id}`}>
                            <td className="py-2.5 px-4">
                              <span className="font-mono text-sm">{nums.length > 1 ? `${nums[0]} +${nums.length - 1}` : nums[0] || '—'}</span>
                            </td>
                            <td className="py-2.5 px-4 text-sm text-muted-foreground">{pr.losing_carrier_name || pr.current_carrier || '—'}</td>
                            <td className="py-2.5 px-4">
                              <Badge className={statusColor[pr.status] || ''}>{statusLabel[pr.status] || pr.status}</Badge>
                            </td>
                            <td className="py-2.5 px-4 text-xs text-muted-foreground">{pr.created_at ? new Date(pr.created_at).toLocaleDateString() : '—'}</td>
                            <td className="py-2.5 px-4 text-right">
                              <Button variant="ghost" size="sm" className="text-xs" onClick={(e) => { e.stopPropagation(); setSelectedPortRequest(pr); }}>View</Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {/* Mobile list */}
                  <div className="sm:hidden divide-y">
                    {portRequests.map((pr) => {
                      const nums = pr.phone_numbers || (pr.phone_number ? [pr.phone_number] : []);
                      return (
                        <div key={pr.id} className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={() => setSelectedPortRequest(pr)} data-testid={`port-card-${pr.id}`}>
                          <div className="min-w-0 flex-1">
                            <span className="font-mono text-sm font-medium">{nums.length > 1 ? `${nums[0]} +${nums.length - 1}` : nums[0] || '—'}</span>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Badge className={`${statusColor[pr.status] || ''} text-[10px]`}>{statusLabel[pr.status] || pr.status}</Badge>
                              {pr.losing_carrier_name && <span className="text-xs text-muted-foreground">{pr.losing_carrier_name}</span>}
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0 ml-2" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ── Usage Tab ── */}
          <TabsContent value="usage" className="mt-4">
            {usage && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { icon: PhoneOutgoing, color: 'text-blue-500', value: usage.call_outbound, label: 'Outbound Calls (min)' },
                  { icon: PhoneIncoming, color: 'text-emerald-500', value: usage.call_inbound, label: 'Inbound Calls (min)' },
                  { icon: MessageSquare, color: 'text-violet-500', value: usage.sms_outbound, label: 'SMS Sent' },
                  { icon: MessageSquare, color: 'text-orange-500', value: usage.sms_inbound, label: 'SMS Received' },
                ].map(({ icon: Icon, color, value, label }) => (
                  <div key={label} className="border rounded-lg p-4 text-center">
                    <Icon className={`w-5 h-5 mx-auto ${color} mb-1.5`} />
                    <p className="text-2xl font-bold">{value}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} onPurchased={load} />
    </Layout>
  );
}
